import { useEffect, useState } from "react";
import { Star, Loader2, CheckCircle2, Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const Card = ({ r }) => (
  <div className="shrink-0 w-[340px] md:w-[420px] mr-5 p-7 rounded-2xl border border-white/10 bg-[#0a0a0a] hover:border-[#3B82F6]/50 transition-colors">
    <div className="flex gap-1 mb-4">
      {Array.from({ length: r.rating }).map((_, i) => (
        <Star key={i} size={14} className="fill-[#3B82F6] text-[#3B82F6]" />
      ))}
    </div>
    <p className="text-zinc-200 leading-relaxed text-[15px]">"{r.text}"</p>
    <div className="mt-6 pt-5 border-t border-white/10">
      <div className="font-display text-lg">{r.name}</div>
      {r.role && <div className="text-xs uppercase tracking-[0.25em] text-zinc-500 mt-1">{r.role}</div>}
    </div>
  </div>
);

const REVIEW_MAX = 600;

export default function Testimonials() {
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(0);
  const [form, setForm] = useState({ name: "", role: "", rating: 5, text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/reviews?ts=${Date.now()}`);
      const list = r.data || [];
      setReviews(list);
      if (list.length) {
        const a = list.reduce((s, x) => s + (x.rating || 0), 0) / list.length;
        setAvg(Math.round(a * 10) / 10);
      } else {
        setAvg(0);
      }
    } catch {
      // silent
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api.post("/reviews", form);
      setSuccess(true);
      setForm({ name: "", role: "", rating: 5, text: "" });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const marqueeList = reviews.length ? [...reviews, ...reviews] : [];

  return (
    <section id="avis" className="relative py-28 lg:py-36 bg-[#050505] overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="reveal flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-[#3B82F6]">Avis clients</div>
            <h2 className="font-display text-5xl md:text-7xl mt-4 leading-[0.9]">Ils ont fait<br />confiance.</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="font-display text-5xl text-[#3B82F6]">{avg > 0 ? avg.toFixed(1) : "—"}</div>
            <div>
              <div className="flex gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className={i < Math.round(avg) ? "fill-[#3B82F6] text-[#3B82F6]" : "text-zinc-700"} />
                ))}
              </div>
              <div className="text-xs uppercase tracking-[0.25em] text-zinc-400 mt-1">
                {reviews.length ? `Sur ${reviews.length} avis` : "Soyez le premier"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {reviews.length > 0 && (
        <div className="mt-14 relative">
          <div className="flex animate-marquee" data-testid="testimonials-marquee">
            {marqueeList.map((r, i) => (
              <Card r={r} key={`${r.id}-${i}`} />
            ))}
          </div>
          <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#050505] to-transparent pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#050505] to-transparent pointer-events-none" />
        </div>
      )}

      {/* Leave a review */}
      <div className="max-w-3xl mx-auto px-6 lg:px-10 mt-16 reveal">
        <div className="glass-strong rounded-2xl p-6 md:p-8" data-testid="review-form-card">
          <div className="text-xs uppercase tracking-[0.4em] text-[#3B82F6]">Laisser un avis</div>
          <div className="font-display text-3xl mt-2">Partagez votre expérience.</div>
          <p className="text-sm text-zinc-400 mt-2">Un mot après votre événement ? Anton adore vous lire.</p>

          {success ? (
            <div className="mt-6 p-5 rounded-xl border border-[#3B82F6]/40 bg-[#3B82F6]/5 flex items-start gap-3" data-testid="review-success">
              <CheckCircle2 className="text-[#3B82F6] shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg">Merci pour votre avis</div>
                <p className="text-sm text-zinc-300 mt-1">Il est publié immédiatement.</p>
                <button onClick={() => setSuccess(false)} className="btn-ghost mt-4 text-xs" data-testid="review-another">
                  Ajouter un autre avis
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="review-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Nom</label>
                  <input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none"
                    data-testid="review-name"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400">Événement (optionnel)</label>
                  <input
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    placeholder="Ex: Mariage · Angoulême"
                    className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none"
                    data-testid="review-role"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 block mb-2">Note</label>
                <div className="flex gap-2" data-testid="review-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      type="button"
                      key={n}
                      onClick={() => setForm({ ...form, rating: n })}
                      aria-label={`${n} étoiles`}
                      className="p-1 transition-transform hover:scale-110"
                      data-testid={`review-star-${n}`}
                    >
                      <Star
                        size={26}
                        className={n <= form.rating ? "fill-[#3B82F6] text-[#3B82F6]" : "text-zinc-600"}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-[0.3em] text-zinc-400 flex justify-between">
                  <span>Votre message</span>
                  <span className={form.text.length > REVIEW_MAX ? "text-red-400" : "text-zinc-500"}>{form.text.length}/{REVIEW_MAX}</span>
                </label>
                <textarea
                  required
                  rows={4}
                  value={form.text}
                  maxLength={REVIEW_MAX}
                  onChange={(e) => setForm({ ...form, text: e.target.value })}
                  placeholder="Racontez votre soirée avec Anton…"
                  className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:border-[#3B82F6] focus:outline-none resize-none"
                  data-testid="review-text"
                />
              </div>

              {error && <div className="text-sm text-red-400" data-testid="review-error">{error}</div>}

              <div className="flex justify-end">
                <button type="submit" disabled={submitting} className="btn-neon disabled:opacity-50" data-testid="review-submit">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Envoi…</> : <>Publier <Send size={16} /></>}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
